import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../zodios/client";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { Button } from "../../components/core/basic/Button";
import { TextField } from "../../components/fields/base/TextField";
import { successToast } from "../../utils/toasts";
import { getAPIKeys } from "../../utils/getUrlPrefix";
import { Link } from "@tanstack/react-router";
import { HoverInfo } from "../../components/core/basic/Tooltip";
import { AuthLayout } from "../../layouts/AuthLayout";
import { useAppDispatch } from "../../redux/hooks";
import { tokenLogin } from "./tokens/tokenLogin";
import { useTranslation } from "react-i18next";

const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

interface UserInvitationFormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

interface AcceptUserInvitationVariables {
  inviteToken: string;
  grant_type: "password";
  client_id: string;
  client_secret: string;
  first_name: string;
  last_name: string;
  password: string;
}

const INITIAL_FORM_DATA: UserInvitationFormData = { firstName: "", lastName: "", password: "", confirmPassword: "" };

const InviteForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [error, setError] = useState("");
  const dispatch = useAppDispatch();
  const { t } = useTranslation("auth");

  const acceptUserInviteMutation = useMutation({
    mutationFn: async (variables: AcceptUserInvitationVariables) => {
      const acceptInviteRes = await apiClient.acceptUserInvitation(variables);

      const passwordLoginInfo = {
        grant_type: "password" as const,
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        username: acceptInviteRes.email_address,
        password: formData.password,
      };

      const tokenRes = await apiClient.login(passwordLoginInfo);
      if ("access_token" in tokenRes) {
        await tokenLogin(tokenRes, dispatch);
        window.location.href = "/user/dashboard";
      }
      if (tokenRes.requires_mfa) {
        setError(t(($) => $.auth.invite["Authentication error, please contact support."]));
      }
    },
    onError: (res: { response: { data: { email?: string[]; password?: string[] } } }) => {
      const errors = res.response.data;

      if (errors.email && errors.email[0]) {
        setError(errors.email[0]);
        return;
      }

      if (errors.password && errors.password[0]) {
        setError(errors.password[0]);
        return;
      }

      setError(
        t(
          ($) =>
            $.auth.invite[
              "Something went wrong while accepting your user invitation. Please try again or contact customer success."
            ],
        ),
      );
    },
    onSuccess: () => {
      successToast({ message: t(($) => $.auth.invite["User invitation successfully accepted."]) });
    },
  });

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <AuthLayout
      title={t(($) => $.auth.invite["Welcome to Trellis"])}
      subtitle={t(($) => $.auth.invite["Complete your invitation by confirming your password."])}
    >
      <form
        data-hs-do-not-collect="true"
        onSubmit={(e) => {
          e.preventDefault();

          const { firstName, lastName, password, confirmPassword } = formData;

          if (password !== confirmPassword) {
            setError(t(($) => $.auth.invite["Password and password confirmation do not match."]));
            return;
          }

          const queryParams = new URLSearchParams(window.location.search);
          const token = queryParams.get("token");

          if (!token) {
            setError(t(($) => $.auth.invite["A token is required"]));
            return;
          }

          acceptUserInviteMutation.mutate({
            grant_type: "password",
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            first_name: firstName,
            last_name: lastName,
            password,
            inviteToken: token,
          });
        }}
      >
        <Flex gap={"medium"} direction={"column"}>
          <TextField
            label={t(($) => $.auth.invite["First Name"])}
            name={"firstName"}
            type={"text"}
            onChange={(value) => handleChange("firstName", value)}
          />
          <TextField
            label={t(($) => $.auth.invite["Last Name"])}
            name={"lastName"}
            type={"text"}
            onChange={(value) => handleChange("lastName", value)}
          />
          <TextField
            label={
              <span className={css({ display: "flex", gap: "small" })}>
                {t(($) => $.auth.invite.Password)}{" "}
                <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                  {t(
                    ($) =>
                      $.auth.invite[
                        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
                      ],
                  )}
                </HoverInfo>
              </span>
            }
            name={"password"}
            type={"password"}
            onChange={(value) => handleChange("password", value)}
          />
          <TextField
            label={t(($) => $.auth.invite["Confirm Password"])}
            name={"confirmPassword"}
            type={"password"}
            onChange={(value) => handleChange("confirmPassword", value)}
          />
        </Flex>
        <Button
          isPending={acceptUserInviteMutation.isPending}
          isDisabled={Object.values(formData).some((fieldValue) => !fieldValue.trim().length)}
          className={css({ width: "100%", marginTop: "large" })}
          type="submit"
        >
          {t(($) => $.auth.invite["Complete Signup"])}
        </Button>
      </form>
      <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
        <Link to="/">{t(($) => $.auth.invite["Sign In Instead"])}</Link>
      </div>
      {error && <p className={css({ paddingY: "large", textAlign: "center" })}>{error}</p>}
    </AuthLayout>
  );
};

export default InviteForm;
